import { useState, useEffect, useRef, useContext, memo } from 'react';
import { useIntl } from 'react-intl';

import { cleanEmptyString, sleep } from 'utils/helpers';

import { LOCALE } from 'constants/intl-wrapper';
import { ContextWrapper } from 'contexts/wrapper.context';

import { customizedFmt } from 'components/IntlWrapper/IntlWrapper';
import BRFormInputs from 'components/BRFormInputs/BRFormInputs';
import BRBilingualOptions from 'components/BRBilingualOptions/BRBilingualOptions';

import { ReactComponent as TextIcon } from 'assets/icons/Text.svg';

import './BRBilingualInput.less';

const BRBilingualInput = ({
  defaultLang,
  placeholderId,
  optional = false,
  inputTitle = '',
  name,
  initialCount,
  children = false,
  onChangeInput = () => {},
  onFocus = () => {},
  onBlur = () => {},
  isTextArea = false,
  defaultInputValue = false,
  ...props
}) => {
  const { businessInfo } = useContext(ContextWrapper);
  const storeInfo = businessInfo?.storeInfo;

  const [inputValue, setInputValue] = useState(
    defaultInputValue || {
      [LOCALE.AR]: null,
      [LOCALE.EN]: null
    }
  );

  const [isInputFocused, setIsInputFocused] = useState(false);
  const [rules, setRules] = useState([]);
  const [choosenFieldLang, setChoosenFieldLang] = useState(
    defaultLang || LOCALE.AR
  );
  const [isFieldTouched, setIsFieldTouched] = useState(false);

  const intl = useIntl();
  const inputRef = useRef();

  useEffect(() => {
    setInputValue(defaultInputValue);
  }, [defaultInputValue]);

  const checkOnInputLang = ({ value }) => {
    setIsFieldTouched(true);
    setInputValue({
      ...inputValue,
      [choosenFieldLang]: value === '' ? undefined : value
    });
    onChangeInput({ value, lang: choosenFieldLang });
    return value;
  };

  const onChangeFieldLanguage = async ({ value }) => {
    setIsFieldTouched(true);
    setChoosenFieldLang(value);
    await sleep(50);
    inputRef?.current?.focus();
    return value;
  };

  function innerObject(o) {
    if (!o) {
      return {};
    }
    return Object.keys(o).reduce(function (r, k) {
      return typeof o[k] === 'object' ? innerObject(o[k]) : ((r[k] = o[k]), r);
    }, {});
  }

  useEffect(() => {
    if (props?.rules) {
      const { rules, formRef } = props;
      const requiredToCheck =
        choosenFieldLang === LOCALE.EN ? name.AR : name.EN;
      const val = formRef?.current?.getFieldsValue([requiredToCheck]);
      const findValue = Array.isArray(requiredToCheck)
        ? requiredToCheck[requiredToCheck.length - 1]
        : requiredToCheck;
      if (val && !!innerObject(val)[findValue]) {
        const isValueFound = cleanEmptyString(innerObject(val));
        if (Object.values(isValueFound)?.length) {
          setRules(() => {
            let newRules = rules.filter((rule) => !rule?.required);
            newRules = [
              { required: false, message: rules[0].message },
              ...newRules
            ];
            return newRules;
          });
        } else {
          setRules(rules);
        }
      } else {
        setRules(rules);
      }
    }
  }, [props?.rules, choosenFieldLang]);

  useEffect(() => {
    if (businessInfo?._id && !isFieldTouched && !defaultLang) {
      setChoosenFieldLang(storeInfo?.defaultLang || LOCALE.AR);
    }
  }, [businessInfo, isFieldTouched, storeInfo?.defaultLang, defaultLang]);

  const handleOnFocus = () => {
    setIsInputFocused(true);
    onFocus();
  };

  const handleOnBlur = () => {
    setIsInputFocused(false);
    inputRef.current.blur();
    onBlur();
  };

  const getLastElementOfObj = (object) => {
    /*eslint no-unreachable-loop: ["error", { "ignore": ["ForInStatement", "ForOfStatement"] }]*/

    if (typeof object !== 'object') {
      return object;
    }
    for (const property in object) {
      return getLastElementOfObj(object[property]);
    }
  };

  useEffect(() => {
    const { formRef } = props;
    const value =
      inputValue.en || inputValue.ar
        ? inputValue
        : formRef
        ? {
            en: getLastElementOfObj(formRef.current.getFieldsValue([name.EN])),
            ar: getLastElementOfObj(formRef.current.getFieldsValue([name.AR]))
          }
        : {};
    if (!isFieldTouched) {
      if (choosenFieldLang === LOCALE.EN && !value.en && value.ar) {
        setChoosenFieldLang(LOCALE.AR);
      } else if (choosenFieldLang === LOCALE.AR && !value.ar && value.en) {
        setChoosenFieldLang(LOCALE.EN);
      }
    }
  }, [isFieldTouched, props, inputValue, name]);

  return (
    <div className="br-bilingual-input">
      <BRFormInputs
        placeholder={
          placeholderId &&
          customizedFmt(choosenFieldLang)({ id: placeholderId })
        }
        name={choosenFieldLang === LOCALE.EN ? name.EN : name.AR}
        initialCount={
          !!initialCount && initialCount[choosenFieldLang]
            ? initialCount[choosenFieldLang]
            : 0
        }
        onChangeInput={checkOnInputLang}
        inputTitle={
          <div className="br-bilingual-input__title">
            <span className="br-bilingual-input__title__left-section">
              {inputTitle}
              {optional && (
                <span className="br-form-optional-label">
                  {intl.formatMessage({
                    id: 'form.optional_label'
                  })}
                </span>
              )}
            </span>
            <BRBilingualOptions
              inputValue={inputValue}
              defaultLang={choosenFieldLang}
              onChangeFieldLanguage={onChangeFieldLanguage}
              isInputFocused={isInputFocused}
              isFieldTouched={isFieldTouched}
              isFromBilingualInput
            />
          </div>
        }
        prefix={<TextIcon />}
        onFocus={handleOnFocus}
        onBlur={handleOnBlur}
        inputRef={inputRef}
        defaultValue={inputValue[choosenFieldLang]}
        {...props}
        rules={rules}
        isTextArea={isTextArea}
      />
      <div className="display-none">
        <BRFormInputs
          name={choosenFieldLang === LOCALE.EN ? name.AR : name.EN}
          isTextArea={isTextArea}
        />
      </div>
    </div>
  );
};

export default memo(BRBilingualInput);
